import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Icon from "../../components/Icon";
import Modal from "../../components/Modal";
import { useFuelStations, useDeleteFuelStations } from "../../api/fuelStations";
import { useFormOptions } from "../../api/queries";
import type { FuelStation } from "../../api/types";
import FuelStationFormModal from "./FuelStationFormModal";

export default function FuelStationsPage() {
  const navigate = useNavigate();
  const stations = useFuelStations();
  const formOptions = useFormOptions();
  const removeMany = useDeleteFuelStations();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const countriesByCode = useMemo(() => {
    const pairs = formOptions.data?.countries ?? [];
    return new Map(pairs.map((c) => [c.code, c.name]));
  }, [formOptions.data?.countries]);

  const groups = useMemo(() => {
    const grouped = new Map<string, FuelStation[]>();
    for (const station of stations.data?.stations ?? []) {
      const list = grouped.get(station.country) ?? [];
      list.push(station);
      grouped.set(station.country, list);
    }
    return Array.from(grouped.entries());
  }, [stations.data?.stations]);

  const isLoading = stations.isLoading || formOptions.isLoading;
  const selectedCount = selectedIds.size;

  const toggleBatchMode = () => {
    if (isBatchMode) {
      setIsBatchMode(false);
      setSelectedIds(new Set());
      setDeleteModalOpen(false);
      setDeleteError(null);
      return;
    }
    setIsBatchMode(true);
  };

  const toggleSelected = (id: number) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    setDeleteError(null);
    try {
      await removeMany.mutateAsync(Array.from(selectedIds));
      setDeleteModalOpen(false);
      setIsBatchMode(false);
      setSelectedIds(new Set());
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not delete stations.";
      setDeleteError(message);
    }
  };

  const headerActions = (
    <>
      {isBatchMode ? (
        <button
          type="button"
          className="button button--ghost car-detail-action history-action--danger"
          aria-label="Delete selected stations"
          title="Delete selected stations"
          disabled={selectedCount === 0 || removeMany.isPending}
          onClick={() => {
            setDeleteError(null);
            setDeleteModalOpen(true);
          }}
        >
          <Icon name="trash" />
        </button>
      ) : null}
      <button
        type="button"
        className="button button--ghost car-detail-action"
        aria-label="Add station"
        title="Add station"
        onClick={() => setIsAddOpen(true)}
      >
        <Icon name="plus" />
      </button>
      <button
        type="button"
        className={
          "button button--ghost car-detail-action" +
          (isBatchMode ? " history-action--active" : "")
        }
        aria-label={isBatchMode ? "Exit selection mode" : "Select stations"}
        title={isBatchMode ? "Exit selection mode" : "Select stations"}
        aria-pressed={isBatchMode}
        disabled={(stations.data?.stations.length ?? 0) === 0 && !isBatchMode}
        onClick={toggleBatchMode}
      >
        <Icon name="checkbox" />
      </button>
    </>
  );

  return (
    <>
      <section className="page-section">
        <div className="car-detail-nav">
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={() => navigate("/account")}
          >
            ← Back to Account
          </button>
        </div>
        <header className="section-header section-header--with-actions">
          <h1 className="section-header__title">Fuel stations</h1>
          <div className="section-header__actions">{headerActions}</div>
        </header>

        {isLoading ? (
          <p className="empty-state">Loading...</p>
        ) : stations.isError || !stations.data ? (
          <p className="empty-state">
            Could not load fuel stations: {stations.error?.message ?? "unknown error"}.
          </p>
        ) : stations.data.stations.length === 0 ? (
          <div className="empty-state">
            <p>No saved fuel stations yet.</p>
            <button
              type="button"
              className="button button--primary"
              onClick={() => setIsAddOpen(true)}
            >
              Add station
            </button>
          </div>
        ) : (
          <div className="station-groups">
            {groups.map(([country, countryStations]) => (
              <section key={country} className="station-group">
                <h2 className="station-group__title">
                  {countriesByCode.get(country) ?? country} [{country}]
                </h2>
                <ul className="station-list">
                  {countryStations.map((station) => (
                    <StationRow
                      key={station.id}
                      station={station}
                      isBatchMode={isBatchMode}
                      selected={selectedIds.has(station.id)}
                      onToggle={() => toggleSelected(station.id)}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </section>

      {formOptions.data ? (
        <FuelStationFormModal
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          countries={formOptions.data.countries}
          defaultCountry={formOptions.data.default_country}
        />
      ) : null}

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (removeMany.isPending) return;
          setDeleteModalOpen(false);
          setDeleteError(null);
        }}
        title="Delete stations"
      >
        <p className="confirm-modal__body">
          Delete {selectedCount} {selectedCount === 1 ? "station" : "stations"}?
        </p>
        {deleteError ? (
          <p className="form-status" data-variant="error" role="alert">
            {deleteError}
          </p>
        ) : null}
        <div className="form-actions">
          <button
            type="button"
            className="button button--ghost"
            disabled={removeMany.isPending}
            onClick={() => {
              setDeleteModalOpen(false);
              setDeleteError(null);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button button--danger"
            disabled={removeMany.isPending || selectedCount === 0}
            onClick={() => void handleDeleteSelected()}
          >
            {removeMany.isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>
    </>
  );
}

function StationRow({
  station,
  isBatchMode,
  selected,
  onToggle,
}: {
  station: FuelStation;
  isBatchMode: boolean;
  selected: boolean;
  onToggle: () => void;
}) {
  const address = [station.street, station.street_number].filter(Boolean).join(" ");
  const rowContent = (
    <>
      {isBatchMode ? (
        <span className="entry-row__select">
          <input
            type="checkbox"
            checked={selected}
            aria-label={`Select ${station.name}`}
            onChange={onToggle}
          />
        </span>
      ) : null}
      <span className="station-row__icon">
        <Icon name="pin" />
      </span>
      <span className="station-row__body">
        <span className="station-row__name">{station.name}</span>
        <span className="station-row__meta">
          {station.city}
          {address ? ` · ${address}` : ""}
        </span>
        {station.notes ? (
          <span className="station-row__notes">{station.notes}</span>
        ) : null}
      </span>
    </>
  );

  if (isBatchMode) {
    return (
      <li>
        <label
          className={
            "station-row station-row--selectable" +
            (selected ? " station-row--selected" : "")
          }
        >
          {rowContent}
        </label>
      </li>
    );
  }

  return <li className="station-row">{rowContent}</li>;
}
